import { validateEmail } from '../validators';
import type { StringKey } from '../strings';

/**
 * Settings › Accounting › Documents Inbox Rules (piece 24; decisions 0251,
 * 1621): the accounting group's shared master mailbox address, the dedicated
 * addresses that pre-flag a classification before extraction runs, and the
 * Subject Line Rules the Inbox evaluates against an incoming subject.
 *
 * The accounting mailbox's reading matches a message's delivered-to addresses
 * against the dedicated addresses and stamps the classification (T3432;
 * decision 9800). 🔨 Nothing runs the subject rules yet (piece 24).
 */

/** The master mailbox's address, one Organization-scoped row that ships unset (decision 0251). */
export const ACCOUNTING_INBOX_ADDRESS_KEY = 'accounting.inboxAddress';

/** What a dedicated address pre-flags, and what a subject rule may be scoped to (piece 24, `DocumentInboxAddress.classification`). */
export const DOCUMENT_INBOX_CLASSIFICATIONS = ['CustomerPO', 'VendorBill', 'VendorQuote', 'Receipt', 'Other'] as const;
export type DocumentInboxClassification = (typeof DOCUMENT_INBOX_CLASSIFICATIONS)[number];

/** 🔒 The words a classification is listed by (T3462): the Documents Inbox Rules page and the mailbox's Looks like block read this one map. */
export const CLASSIFICATION_WORD = {
  CustomerPO: 'settings.documentsInbox.classification.customerPO',
  VendorBill: 'settings.documentsInbox.classification.vendorBill',
  VendorQuote: 'settings.documentsInbox.classification.vendorQuote',
  Receipt: 'settings.documentsInbox.classification.receipt',
  Other: 'settings.documentsInbox.classification.other',
} as const satisfies Record<DocumentInboxClassification, StringKey>;

export const SUBJECT_LINE_MATCH_KINDS = ['BracketedReference', 'Word'] as const;
export type SubjectLineMatchKind = (typeof SUBJECT_LINE_MATCH_KINDS)[number];

export const SUBJECT_LINE_ACTIONS = ['LinkToRecord', 'SetPriorityFlag'] as const;
export type SubjectLineAction = (typeof SUBJECT_LINE_ACTIONS)[number];

/** Draft matches nothing, so a rule is written and reviewed before it is switched on. */
export const SUBJECT_LINE_RULE_STATUSES = ['Active', 'Draft'] as const;
export type SubjectLineRuleStatus = (typeof SUBJECT_LINE_RULE_STATUSES)[number];

export interface DocumentInboxAddressRow {
  id: string;
  inboxAddress: string;
  classification: DocumentInboxClassification;
}

export interface SubjectLineRuleRow {
  id: string;
  version: number;
  token: string;
  matchKind: SubjectLineMatchKind;
  documentType: DocumentInboxClassification | null;
  action: SubjectLineAction;
  priority: number;
  status: SubjectLineRuleStatus;
}

/** What `GET /banking/settings/documents-inbox` answers. */
export interface DocumentsInboxAnswer {
  /** The master mailbox's address, or null while it is unset. */
  inboxAddress: string | null;
  /** The name of the mailbox answering at that address, or null when none does. */
  mailboxName: string | null;
  /** A super admin's alone, because the edit renames the mailbox it names (decision 1621). */
  canWriteInboxAddress: boolean;
  /** The active dedicated addresses, in address order; a retired one is kept and not listed. */
  addresses: DocumentInboxAddressRow[];
  /** The rules in priority order. */
  rules: SubjectLineRuleRow[];
}

export interface NewSubjectLineRule {
  token: string;
  matchKind: SubjectLineMatchKind;
  documentType: DocumentInboxClassification | null;
  action: SubjectLineAction;
  priority: number;
  status: SubjectLineRuleStatus;
}

/** The address as stored: trimmed and lower-cased, as a mailbox stores it. */
export function normalizeInboxAddress(value: string): string {
  return value.trim().toLowerCase();
}

/** The refusal a typed address earns, or null; the same shape rule every email box uses. */
export function inboxAddressProblem(value: unknown): string | null {
  if (typeof value !== 'string' || value.trim() === '') return 'Type an email address, such as bills@example.com.';
  const outcome = validateEmail(value, 'US');
  return outcome.ok ? null : outcome.message;
}

export function isDocumentInboxClassification(value: unknown): value is DocumentInboxClassification {
  return typeof value === 'string' && (DOCUMENT_INBOX_CLASSIFICATIONS as readonly string[]).includes(value);
}

/** The refusal a new rule earns, or null: a token of one word, the kinds the piece declares, and a priority of 1 or more. */
export function subjectLineRuleProblem(input: Partial<Record<keyof NewSubjectLineRule, unknown>>): string | null {
  const token = typeof input.token === 'string' ? input.token.trim() : '';
  if (token === '') return 'A rule names the token it matches, such as URGENT or PO.';
  if (/\s/.test(token)) return 'A token is one word with no spaces.';
  if (token.length > 40) return 'A token is 40 characters or fewer.';
  if (!(SUBJECT_LINE_MATCH_KINDS as readonly unknown[]).includes(input.matchKind)) return 'A rule matches a bracketed reference or a word.';
  if (input.documentType !== null && input.documentType !== undefined && !isDocumentInboxClassification(input.documentType)) return 'A rule is scoped to one document type, or to any.';
  if (!(SUBJECT_LINE_ACTIONS as readonly unknown[]).includes(input.action)) return 'A rule links to a record or sets the priority flag.';
  if (typeof input.priority !== 'number' || !Number.isInteger(input.priority) || input.priority < 1) return 'A rule’s priority is a whole number of 1 or more.';
  if (!(SUBJECT_LINE_RULE_STATUSES as readonly unknown[]).includes(input.status)) return 'A rule is Active or Draft.';
  return null;
}
